import {
  useFonts,
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold
} from "@expo-google-fonts/inter";
import {
  MaterialSymbolsOutlined_400Regular,
  MaterialSymbolsOutlined_500Medium,
  MaterialSymbolsOutlined_600SemiBold,
  MaterialSymbolsOutlined_700Bold
} from "@expo-google-fonts/material-symbols-outlined";
import * as SplashScreen from "expo-splash-screen";
import { useEffect } from "react";
import { Platform } from "react-native";
import { AuthProvider, useAuth } from "@/hooks/auth";
import { Slot } from "expo-router";
import { Toast } from "@/components";
import { brand } from "@/constants";
import Head from "expo-router/head";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { initSentry, setSentryUser } from "@/utils/sentry";

// Initialize Sentry as early as possible so unhandled errors during the
// initial render are captured. No-op if EXPO_PUBLIC_SENTRY_DSN is not set.
initSentry();

// Prevent splash screen from auto-hiding
SplashScreen.preventAutoHideAsync();

function RootLayoutNav() {
  // Tag subsequent Sentry events with the Firebase UID (id only — never email).
  // Route protection lives in (drawer)/_layout.tsx as a render-time AuthGate;
  // public routes (terms, privacy, crisis, waitlist) sit outside (drawer) and
  // need no gate. The root index.tsx redirects into the drawer.
  const { user } = useAuth();
  useEffect(() => {
    setSentryUser(user?.uid ?? null);
  }, [user?.uid]);

  return <Slot />;
}

export default function RootLayout() {
  // Handle domain redirect for web platform
  useEffect(() => {
    if (Platform.OS === "web") {
      const hostname = window.location.hostname;

      // Redirect only if on the old web.app domain
      if (hostname === "serenity-now-ai.web.app") {
        // Preserve pathname and search params
        const newUrl = `https://app.serenit.ai${window.location.pathname}${window.location.search}`;
        window.location.replace(newUrl); // 301-like behavior
      }
    }
  }, []);

  const [fontsLoaded, fontError] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    MaterialSymbolsOutlined_400Regular,
    MaterialSymbolsOutlined_500Medium,
    MaterialSymbolsOutlined_600SemiBold,
    MaterialSymbolsOutlined_700Bold
  });

  useEffect(() => {
    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded, fontError]);

  if (!fontsLoaded && !fontError) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Head>
        <title>{brand.name}</title>
        <meta name="description" content={`${brand.name} Assistant`} />
      </Head>
      <AuthProvider>
        <RootLayoutNav />
      </AuthProvider>
      <Toast />
    </GestureHandlerRootView>
  );
}
